import { useMemo, useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Download, Loader2, ChevronLeft, ChevronRight, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AuditFilters } from '@/components/app/audit-filters';
import { AuditTable } from '@/components/app/audit-table';
import { AuditDrawer } from '@/components/app/audit-drawer';
import { apiFetch } from '@/lib/api';
import { useIsMobile } from '@/hooks/use-media-query';
import { useTranslation } from 'react-i18next';
import { auditListResponseSchema, toAuditEvent } from '@/schemas/audit';
import { dashboardActivationsSchema } from '@/schemas/dashboard';
import type { AuditEvent, AuditListResponse } from '@/schemas/audit';
import type { ActivationItem } from '@/types/api';
import { toCsv } from '@/lib/csv';
import type { SortingState } from '@tanstack/react-table';

function mapActivation(a: ActivationItem): AuditEvent {
  return {
    id: a.id,
    licenseId: a.licenseId,
    serial: a.serial,
    event: 'activated',
    performedBy: 'api',
    ipAddress: '-',
    createdAt: a.activatedAt,
    metadata: { fingerprintHash: a.fingerprintHash, lastSeen: a.lastSeen },
  };
}

async function fetchAuditPage(ep: string): Promise<AuditListResponse> {
  const resp = await apiFetch(ep);
  const json = await resp.json();
  const page = auditListResponseSchema.parse(json);
  return { ...page, data: page.data.map(toAuditEvent) };
}

async function fetchActivationsPage(ep: string): Promise<AuditListResponse> {
  const resp = await apiFetch(ep);
  const json = await resp.json();
  const p = dashboardActivationsSchema.parse(json);
  return {
    data: p.data.map(mapActivation),
    total: p.total,
    page: p.page,
    limit: p.limit,
    pages: p.pages,
  };
}

function buildEndpoint(sp: URLSearchParams, audit: boolean): string {
  const p = new URLSearchParams();
  p.set('page', sp.get('page') ?? '1');
  p.set('limit', sp.get('limit') ?? '25');

  const serial = sp.get('serial');
  if (serial) p.set('serial', serial);

  if (audit) {
    const e = sp.get('event');
    if (e) p.set('event', e);
    const from = sp.get('from');
    if (from) p.set('from', from);
    const to = sp.get('to');
    if (to) p.set('to', to);
    const actor = sp.get('actor');
    if (actor) p.set('actor', actor);
  }

  const sort = sp.get('sort');
  if (sort) p.set('sort', sort);

  const base = audit ? '/licenses/audit' : '/dashboard/activations';
  return `${base}?${p.toString()}`;
}

export default function AuditPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const [selected, setSelected] = useState<AuditEvent | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const isMobile = useIsMobile();
  const probeRef = useRef<'audit' | 'activations' | null>(null);

  const listQ = useQuery({
    queryKey: ['audit', 'list', searchParams.toString()],
    queryFn: async (): Promise<AuditListResponse> => {
      if (!probeRef.current) {
        try {
          const r = await apiFetch('/licenses/audit?limit=1');
          probeRef.current = r.ok ? 'audit' : 'activations';
        } catch {
          probeRef.current = 'activations';
        }
      }
      const mode = probeRef.current;
      const ep = buildEndpoint(searchParams, mode === 'audit');
      if (mode === 'audit') {
        return fetchAuditPage(ep);
      }
      return fetchActivationsPage(ep);
    },
    staleTime: 30_000,
  });

  const sorting: SortingState = useMemo(() => {
    const sort = searchParams.get('sort');
    if (!sort) return [{ id: 'createdAt', desc: true }];
    const [id, desc] = sort.split(':');
    if (!id) return [{ id: 'createdAt', desc: true }];
    return [{ id, desc: desc === 'desc' }];
  }, [searchParams]);

  const setSorting = (next: SortingState) => {
    const first = next[0];
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (first) p.set('sort', `${first.id}:${first.desc ? 'desc' : 'asc'}`);
      else p.delete('sort');
      return p;
    });
  };

  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10));
  const limit = Math.max(1, parseInt(searchParams.get('limit') ?? '25', 10));

  const setPage = (n: number) =>
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('page', String(n));
      return p;
    });

  const setLimit = (n: number) =>
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('limit', String(n));
      p.set('page', '1');
      return p;
    });

  const data = listQ.data;
  const isFirstLoad = listQ.isLoading && !data;
  const isRefetching = listQ.isFetching && !listQ.isLoading;
  const total = data?.total ?? 0;
  const pages = data?.pages ?? 1;
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const activeFilterCount = ['serial', 'actor', 'event', 'from', 'to'].reduce(
    (n, k) => n + ((searchParams.get(k) ?? '').length > 0 ? 1 : 0),
    0
  );
  const hasFilters = activeFilterCount > 0;

  const clearFilters = () => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      ['serial', 'actor', 'event', 'from', 'to'].forEach((k) => p.delete(k));
      p.set('page', '1');
      return p;
    });
  };

  const handleExport = async () => {
    if (!data || probeRef.current === null) return;
    const cap = 5000;
    if (total > cap && !window.confirm(t('audit.exportConfirm', { count: cap }))) return;

    setExporting(true);
    try {
      const all: AuditEvent[] = [];
      const maxPages = Math.min(Math.ceil(cap / limit), Math.ceil(total / limit));
      for (let pg = 1; pg <= maxPages; pg++) {
        const sp = new URLSearchParams(searchParams);
        sp.set('page', String(pg));
        sp.set('limit', String(limit));
        const ep = buildEndpoint(sp, probeRef.current === 'audit');
        const resp =
          probeRef.current === 'audit' ? await fetchAuditPage(ep) : await fetchActivationsPage(ep);
        all.push(...resp.data);
        if (all.length >= cap) break;
      }
      const rows = all.slice(0, cap).map((r) => ({
        when: r.createdAt,
        event: r.event,
        serial: r.serial,
        by: r.performedBy,
        from: r.ipAddress ?? '',
      }));
      const csv = toCsv(rows, ['when', 'event', 'serial', 'by', 'from']);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  return (
    <div className="mx-auto w-full max-w-[1120px]">
      {/* Page head */}
      <div className="flex items-start justify-between gap-8">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight text-foreground">
            {t('audit.title')}
          </h1>
          <p className="mt-2.5 max-w-[52ch] font-serif text-[17.5px] italic leading-relaxed text-muted-foreground">
            {t('audit.subtitle')}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleExport}
          disabled={!data || exporting || total === 0}
          className="mt-2.5 shrink-0 gap-2"
        >
          {exporting ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Download className="h-4 w-4" aria-hidden="true" />
          )}
          {t('audit.exportCsv')}
        </Button>
      </div>

      <div className="mt-8 md:flex md:items-start md:gap-8">
        {/* Filter rail — integrated: hairline separator, sticky, collapsible on mobile */}
        {isMobile ? (
          <div className="mb-4">
            <Button
              variant="outline"
              size="sm"
              className="h-9 gap-1.5 border-input bg-transparent text-[13.5px] font-medium text-muted-foreground hover:text-foreground"
              onClick={() => setFiltersOpen((v) => !v)}
              aria-expanded={filtersOpen}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
              {t('common.filtersToggle')}
              {activeFilterCount > 0 && (
                <span className="inline-block min-w-[18px] rounded-full bg-primary/15 px-1.5 py-px text-center text-[11px] font-semibold text-primary">
                  {activeFilterCount}
                </span>
              )}
            </Button>
            {filtersOpen && (
              <div className="mt-3 rounded-lg border px-4 py-5">
                <AuditFilters />
              </div>
            )}
          </div>
        ) : (
          <aside className="sticky top-0 w-[212px] shrink-0 self-start border-r pr-6 pt-1">
            <AuditFilters />
          </aside>
        )}

        <div className="min-w-0 flex-1">
          {listQ.isError ? (
            <div className="flex items-center justify-between rounded-lg border border-destructive/50 bg-destructive/10 p-4">
              <p className="text-sm text-destructive">{t('audit.loadError')}</p>
              <Button variant="outline" size="sm" onClick={() => listQ.refetch()}>
                {t('common.retry')}
              </Button>
            </div>
          ) : isFirstLoad ? (
            <div className="space-y-2">
              <div className="flex gap-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={`h-${i}`} className="h-9 flex-1" />
                ))}
              </div>
              {Array.from({ length: 3 }).map((_, ri) => (
                <div key={ri} className="flex gap-2">
                  {Array.from({ length: 5 }).map((_, ci) => (
                    <Skeleton key={`${ri}-${ci}`} className="h-11 flex-1" />
                  ))}
                </div>
              ))}
            </div>
          ) : total === 0 ? (
            hasFilters ? (
              <div className="flex flex-col items-center justify-center gap-3 py-20">
                <p className="text-[15px] text-muted-foreground">{t('audit.noMatch')}</p>
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  {t('common.clearFilters')}
                </Button>
              </div>
            ) : (
              <div className="flex h-[45vh] flex-col items-center justify-center gap-4">
                <h2 className="font-serif text-2xl italic tracking-tight text-foreground">
                  {t('audit.emptyTitle')}
                </h2>
                <p className="max-w-[44ch] text-center text-[14.5px] text-muted-foreground">
                  {t('audit.emptyHint')}
                </p>
              </div>
            )
          ) : (
            <div
              className={`transition-opacity ${isRefetching ? 'pointer-events-none opacity-60' : ''}`}
            >
              <AuditTable
                data={data?.data ?? []}
                sorting={sorting}
                onSortingChange={setSorting}
                onRowClick={(row) => {
                  setSelected(row);
                  setDrawerOpen(true);
                }}
              />
            </div>
          )}

          {total > 0 && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-4">
              <span className="text-[13px] tabular-nums text-faint">
                {t('common.showingRange', { start, end, total })}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 border-input bg-transparent"
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                  aria-label={t('common.previousPage')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="min-w-[64px] text-center text-[13px] tabular-nums text-muted-foreground">
                  {page} / {pages}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 border-input bg-transparent"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= pages}
                  aria-label={t('common.nextPage')}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Select value={String(limit)} onValueChange={(v) => setLimit(parseInt(v, 10))}>
                  <SelectTrigger
                    className="h-8 w-[72px] border-input bg-transparent text-[13px]"
                    aria-label={t('common.pageSize')}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
      </div>

      <AuditDrawer
        event={selected}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelected(null);
        }}
      />
    </div>
  );
}

export const handle = { crumb: 'Audit' };
